import type { Photo } from './api';

/** Recent authorized album listings; image bytes remain in the photo cache. */
const DATABASE_NAME = 'family-photos-private-album-photos-v1';
const STORE_NAME = 'albums';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_ALBUMS = 6;
const MAX_PHOTOS = 120;

interface AlbumPhotoEntry {
  key: string;
  userId: string;
  albumPath: string;
  cachedAt: number;
  expiresAt: number;
  photos: Photo[];
}

export async function readCachedAlbumPhotos(userId: string, albumPath: string): Promise<Photo[] | null> {
  const database = await openDatabase();
  if (!database) return null;
  return new Promise((resolve) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(cacheKey(userId, albumPath));
    request.onsuccess = () => {
      const entry = request.result as AlbumPhotoEntry | undefined;
      if (!entry || entry.userId !== userId || entry.albumPath !== albumPath || entry.expiresAt <= Date.now()) {
        if (entry) store.delete(entry.key);
        resolve(null);
        return;
      }
      resolve(sanitizePhotos(entry.photos));
    };
    request.onerror = () => resolve(null);
    transaction.oncomplete = () => database.close();
    transaction.onerror = () => { database.close(); resolve(null); };
    transaction.onabort = () => { database.close(); resolve(null); };
  });
}

export async function writeCachedAlbumPhotos(userId: string, albumPath: string, photos: Photo[]): Promise<void> {
  if (!userId || !albumPath) return;
  const database = await openDatabase();
  if (!database) return;
  const now = Date.now();
  await runTransaction(database, (store) => store.put({
    key: cacheKey(userId, albumPath),
    userId,
    albumPath,
    cachedAt: now,
    expiresAt: now + CACHE_TTL_MS,
    photos: sanitizePhotos(photos),
  } satisfies AlbumPhotoEntry));
  await pruneAlbumPhotoCache(userId);
}

export async function clearAlbumPhotoCache(): Promise<void> {
  const database = await openDatabase();
  if (!database) return;
  await runTransaction(database, (store) => store.clear());
}

export async function purgeAlbumPhotoCacheExcept(userId: string): Promise<void> {
  const database = await openDatabase();
  if (!database) return;
  await runTransaction(database, (store) => {
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      if ((cursor.value as Partial<AlbumPhotoEntry>).userId !== userId) cursor.delete();
      cursor.continue();
    };
  });
}

async function pruneAlbumPhotoCache(userId: string): Promise<void> {
  const database = await openDatabase();
  if (!database) return;
  await runTransaction(database, (store) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const entries = (request.result as AlbumPhotoEntry[])
        .filter((entry) => entry.userId === userId)
        .sort((a, b) => b.cachedAt - a.cachedAt);
      entries.slice(MAX_ALBUMS).forEach((entry) => store.delete(entry.key));
    };
  });
}

function sanitizePhotos(photos: Photo[]): Photo[] {
  if (!Array.isArray(photos)) return [];
  return photos.filter((photo) => (
    photo
    && typeof photo.fileId === 'string'
    && typeof photo.name === 'string'
    && typeof photo.path === 'string'
    && typeof photo.contentType === 'string'
    && typeof photo.lastModified === 'number'
  )).map((photo) => ({
    fileId: photo.fileId,
    name: photo.name,
    path: photo.path,
    contentType: photo.contentType,
    size: Number.isFinite(photo.size) ? photo.size : 0,
    lastModified: photo.lastModified,
    hasPreview: Boolean(photo.hasPreview),
    owner: typeof photo.owner === 'string' ? photo.owner : 'Family',
    isVideo: Boolean(photo.isVideo),
    ...(typeof photo.album === 'string' ? { album: photo.album } : {}),
  })).slice(0, MAX_PHOTOS);
}

function cacheKey(userId: string, albumPath: string): string {
  return `${encodeURIComponent(userId)}:${encodeURIComponent(albumPath)}`;
}

function openDatabase(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    const request = indexedDB.open(DATABASE_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
}

function runTransaction(
  database: IDBDatabase,
  operation: (store: IDBObjectStore) => IDBRequest | void,
): Promise<void> {
  return new Promise((resolve) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    try {
      operation(transaction.objectStore(STORE_NAME));
    } catch {
      database.close();
      resolve();
      return;
    }
    transaction.oncomplete = () => { database.close(); resolve(); };
    transaction.onerror = () => { database.close(); resolve(); };
    transaction.onabort = () => { database.close(); resolve(); };
  });
}
